// Safeguarding review (Care Act 2014 s42) case spine — one alert → one review.
// The drawer creates a review from a safeguarding governance alert, then saves
// each s42 step independently against the persisted case (triage → notify →
// lead → enquiry → plan → outcome).

// LIST — the resident's cases (alert-born + manual, open + closed) for the
// workspace list
export const LIST_SAFEGUARDING_REVIEWS =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_REVIEWS";
export const LIST_SAFEGUARDING_REVIEWS_SUCCESS =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_REVIEWS_SUCCESS";
export const LIST_SAFEGUARDING_REVIEWS_FAILURE =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_REVIEWS_FAILURE";

// CASES — open case references in the location, for the "link to existing case"
// picker (multi-resident incidents)
export const LIST_SAFEGUARDING_CASES =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_CASES";
export const LIST_SAFEGUARDING_CASES_SUCCESS =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_CASES_SUCCESS";
export const LIST_SAFEGUARDING_CASES_FAILURE =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_CASES_FAILURE";

// HISTORY — this resident's prior safeguarding incidents + reviews (link picker
// in the enquiry step)
export const LIST_SAFEGUARDING_HISTORY =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_HISTORY";
export const LIST_SAFEGUARDING_HISTORY_SUCCESS =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_HISTORY_SUCCESS";
export const LIST_SAFEGUARDING_HISTORY_FAILURE =
  "app/SAFEGUARDING_REVIEW/LIST_SAFEGUARDING_HISTORY_FAILURE";

// GET — hydrate the drawer (review + notifications + linked plans)
export const GET_SAFEGUARDING_REVIEW =
  "app/SAFEGUARDING_REVIEW/GET_SAFEGUARDING_REVIEW";
export const GET_SAFEGUARDING_REVIEW_SUCCESS =
  "app/SAFEGUARDING_REVIEW/GET_SAFEGUARDING_REVIEW_SUCCESS";
export const GET_SAFEGUARDING_REVIEW_FAILURE =
  "app/SAFEGUARDING_REVIEW/GET_SAFEGUARDING_REVIEW_FAILURE";

// CREATE — start the case from an alert (triage step)
export const CREATE_SAFEGUARDING_REVIEW =
  "app/SAFEGUARDING_REVIEW/CREATE_SAFEGUARDING_REVIEW";
export const CREATE_SAFEGUARDING_REVIEW_SUCCESS =
  "app/SAFEGUARDING_REVIEW/CREATE_SAFEGUARDING_REVIEW_SUCCESS";
export const CREATE_SAFEGUARDING_REVIEW_FAILURE =
  "app/SAFEGUARDING_REVIEW/CREATE_SAFEGUARDING_REVIEW_FAILURE";

// PATCH — idempotent per-step save (triage / lead / capacity-reference / outcome)
export const PATCH_SAFEGUARDING_REVIEW =
  "app/SAFEGUARDING_REVIEW/PATCH_SAFEGUARDING_REVIEW";
export const PATCH_SAFEGUARDING_REVIEW_SUCCESS =
  "app/SAFEGUARDING_REVIEW/PATCH_SAFEGUARDING_REVIEW_SUCCESS";
export const PATCH_SAFEGUARDING_REVIEW_FAILURE =
  "app/SAFEGUARDING_REVIEW/PATCH_SAFEGUARDING_REVIEW_FAILURE";

// NOTIFICATION — add a statutory notification row (LA / CQC / police / ICB / other)
export const ADD_SAFEGUARDING_NOTIFICATION =
  "app/SAFEGUARDING_REVIEW/ADD_SAFEGUARDING_NOTIFICATION";
export const ADD_SAFEGUARDING_NOTIFICATION_SUCCESS =
  "app/SAFEGUARDING_REVIEW/ADD_SAFEGUARDING_NOTIFICATION_SUCCESS";
export const ADD_SAFEGUARDING_NOTIFICATION_FAILURE =
  "app/SAFEGUARDING_REVIEW/ADD_SAFEGUARDING_NOTIFICATION_FAILURE";

// LINK-RECORD — instantiate + link a risk-assessment / care-plan / MCA / DoLS
export const LINK_SAFEGUARDING_RECORD =
  "app/SAFEGUARDING_REVIEW/LINK_SAFEGUARDING_RECORD";
export const LINK_SAFEGUARDING_RECORD_SUCCESS =
  "app/SAFEGUARDING_REVIEW/LINK_SAFEGUARDING_RECORD_SUCCESS";
export const LINK_SAFEGUARDING_RECORD_FAILURE =
  "app/SAFEGUARDING_REVIEW/LINK_SAFEGUARDING_RECORD_FAILURE";

// RESOLVE — outcome + review-due; auto-resolves the source alert (warn+override)
export const RESOLVE_SAFEGUARDING_REVIEW =
  "app/SAFEGUARDING_REVIEW/RESOLVE_SAFEGUARDING_REVIEW";
export const RESOLVE_SAFEGUARDING_REVIEW_SUCCESS =
  "app/SAFEGUARDING_REVIEW/RESOLVE_SAFEGUARDING_REVIEW_SUCCESS";
export const RESOLVE_SAFEGUARDING_REVIEW_FAILURE =
  "app/SAFEGUARDING_REVIEW/RESOLVE_SAFEGUARDING_REVIEW_FAILURE";

// ---------------------------------------------------------------------------
// INCIDENT ↔ CASE linkage. One `rd_incident_record` belongs to ONE safeguarding
// review; one review holds MANY incidents. These are REAL incident records —
// distinct from LIST_SAFEGUARDING_HISTORY above, which returns governance
// ALERTS (`rd_governance_item` uuids) and is only ever a display list.
// ---------------------------------------------------------------------------

// LINKABLE — incident records in this resident's record that may still be
// attached to this review (nothing already linked to another case)
export const LIST_LINKABLE_INCIDENTS =
  "app/SAFEGUARDING_REVIEW/LIST_LINKABLE_INCIDENTS";
export const LIST_LINKABLE_INCIDENTS_SUCCESS =
  "app/SAFEGUARDING_REVIEW/LIST_LINKABLE_INCIDENTS_SUCCESS";
export const LIST_LINKABLE_INCIDENTS_FAILURE =
  "app/SAFEGUARDING_REVIEW/LIST_LINKABLE_INCIDENTS_FAILURE";

// LINK — attach one incident record to this review (optional link_note)
export const LINK_SAFEGUARDING_INCIDENT =
  "app/SAFEGUARDING_REVIEW/LINK_SAFEGUARDING_INCIDENT";
export const LINK_SAFEGUARDING_INCIDENT_SUCCESS =
  "app/SAFEGUARDING_REVIEW/LINK_SAFEGUARDING_INCIDENT_SUCCESS";
export const LINK_SAFEGUARDING_INCIDENT_FAILURE =
  "app/SAFEGUARDING_REVIEW/LINK_SAFEGUARDING_INCIDENT_FAILURE";

// UNLINK — detach an incident. The api 422s on the originating incident, so the
// UI hides the action on the `is_origin` row rather than relying on the guard.
export const UNLINK_SAFEGUARDING_INCIDENT =
  "app/SAFEGUARDING_REVIEW/UNLINK_SAFEGUARDING_INCIDENT";
export const UNLINK_SAFEGUARDING_INCIDENT_SUCCESS =
  "app/SAFEGUARDING_REVIEW/UNLINK_SAFEGUARDING_INCIDENT_SUCCESS";
export const UNLINK_SAFEGUARDING_INCIDENT_FAILURE =
  "app/SAFEGUARDING_REVIEW/UNLINK_SAFEGUARDING_INCIDENT_FAILURE";

// CASE — one event, several residents: every review + incident carrying one
// shared `reference`. Read-only; the case view GATHERS, it never merges.
export const GET_SAFEGUARDING_CASE =
  "app/SAFEGUARDING_REVIEW/GET_SAFEGUARDING_CASE";
export const GET_SAFEGUARDING_CASE_SUCCESS =
  "app/SAFEGUARDING_REVIEW/GET_SAFEGUARDING_CASE_SUCCESS";
export const GET_SAFEGUARDING_CASE_FAILURE =
  "app/SAFEGUARDING_REVIEW/GET_SAFEGUARDING_CASE_FAILURE";
